/**
 * Annotation API Hooks
 *
 * TanStack Query hooks for annotation sets and annotations.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAnnotationSetsApiV1DatasetsDatasetIdAnnotationSetsGet,
  createAnnotationSetApiV1DatasetsDatasetIdAnnotationSetsPost,
  getAnnotationSetDetailApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdGet,
  deleteAnnotationSetApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdDelete,
  publishAnnotationSetApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdPublishPost,
  listAnnotationsApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsGet,
  createAnnotationApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsPost,
  createAnnotationsBulkApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsBulkPost,
  updateAnnotationApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsAnnotationIdPatch,
  deleteAnnotationApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsAnnotationIdDelete,
} from '@/api';
import type {
  AnnotationCreate,
  AnnotationUpdate,
  AnnotationBulkCreate,
} from '@/api';
import { datasetKeys } from './datasets';
import { useAuthEnabled } from './useAuthQuery';

// Query keys factory
export const annotationSetKeys = {
  all: ['annotationSets'] as const,
  lists: () => [...annotationSetKeys.all, 'list'] as const,
  list: (datasetId: string) =>
    [...annotationSetKeys.lists(), datasetId] as const,
  details: () => [...annotationSetKeys.all, 'detail'] as const,
  detail: (datasetId: string, setId: string) =>
    [...annotationSetKeys.details(), datasetId, setId] as const,
};

export const annotationKeys = {
  all: ['annotations'] as const,
  lists: () => [...annotationKeys.all, 'list'] as const,
  list: (datasetId: string, setId: string) =>
    [...annotationKeys.lists(), datasetId, setId] as const,
};

/**
 * List annotation sets for a dataset.
 */
export function useAnnotationSets(datasetId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(datasetId));

  return useQuery({
    queryKey: annotationSetKeys.list(datasetId),
    queryFn: async () => {
      const response =
        await listAnnotationSetsApiV1DatasetsDatasetIdAnnotationSetsGet({
          path: { dataset_id: datasetId },
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch annotation sets'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Get annotation set with all annotations.
 */
export function useAnnotationSet(datasetId: string, annotationSetId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(datasetId) && Boolean(annotationSetId));

  return useQuery({
    queryKey: annotationSetKeys.detail(datasetId, annotationSetId),
    queryFn: async () => {
      const response =
        await getAnnotationSetDetailApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdGet(
          {
            path: {
              dataset_id: datasetId,
              annotation_set_id: annotationSetId,
            },
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch annotation set'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Create a new annotation set (new version).
 */
export function useCreateAnnotationSet(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response =
        await createAnnotationSetApiV1DatasetsDatasetIdAnnotationSetsPost({
          path: { dataset_id: datasetId },
        });
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to create annotation set'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationSetKeys.list(datasetId),
      });
      queryClient.invalidateQueries({
        queryKey: datasetKeys.detail(datasetId),
      });
    },
  });
}

/**
 * Delete an annotation set (drafts only).
 */
export function useDeleteAnnotationSet(datasetId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (annotationSetId: string) => {
      const response =
        await deleteAnnotationSetApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdDelete(
          {
            path: {
              dataset_id: datasetId,
              annotation_set_id: annotationSetId,
            },
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to delete annotation set'
        );
      }
      return response.data;
    },
    onSuccess: (_, annotationSetId) => {
      queryClient.removeQueries({
        queryKey: annotationSetKeys.detail(datasetId, annotationSetId),
      });
      queryClient.invalidateQueries({
        queryKey: annotationSetKeys.list(datasetId),
      });
    },
  });
}

/**
 * Publish an annotation set (lock for training).
 */
export function usePublishAnnotationSet(
  datasetId: string,
  annotationSetId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response =
        await publishAnnotationSetApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdPublishPost(
          {
            path: {
              dataset_id: datasetId,
              annotation_set_id: annotationSetId,
            },
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to publish annotation set'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationSetKeys.detail(datasetId, annotationSetId),
      });
      queryClient.invalidateQueries({
        queryKey: annotationSetKeys.list(datasetId),
      });
    },
  });
}

/**
 * List annotations for an annotation set.
 */
export function useAnnotations(datasetId: string, annotationSetId: string) {
  const { enabled: authEnabled } = useAuthEnabled(Boolean(datasetId) && Boolean(annotationSetId));

  return useQuery({
    queryKey: annotationKeys.list(datasetId, annotationSetId),
    queryFn: async () => {
      const response =
        await listAnnotationsApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsGet(
          {
            path: {
              dataset_id: datasetId,
              annotation_set_id: annotationSetId,
            },
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to fetch annotations'
        );
      }
      return response.data;
    },
    enabled: authEnabled,
  });
}

/**
 * Create a single annotation.
 */
export function useCreateAnnotation(
  datasetId: string,
  annotationSetId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AnnotationCreate) => {
      const response =
        await createAnnotationApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsPost(
          {
            path: {
              dataset_id: datasetId,
              annotation_set_id: annotationSetId,
            },
            body: data,
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to create annotation'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.list(datasetId, annotationSetId),
      });
      queryClient.invalidateQueries({
        queryKey: annotationSetKeys.detail(datasetId, annotationSetId),
      });
    },
  });
}

/**
 * Create multiple annotations at once.
 */
export function useCreateAnnotationsBulk(
  datasetId: string,
  annotationSetId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: AnnotationBulkCreate) => {
      const response =
        await createAnnotationsBulkApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsBulkPost(
          {
            path: {
              dataset_id: datasetId,
              annotation_set_id: annotationSetId,
            },
            body: data,
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to create annotations'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.list(datasetId, annotationSetId),
      });
      queryClient.invalidateQueries({
        queryKey: annotationSetKeys.detail(datasetId, annotationSetId),
      });
    },
  });
}

/**
 * Update an annotation.
 */
export function useUpdateAnnotation(
  datasetId: string,
  annotationSetId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      annotationId,
      data,
    }: {
      annotationId: string;
      data: AnnotationUpdate;
    }) => {
      const response =
        await updateAnnotationApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsAnnotationIdPatch(
          {
            path: {
              dataset_id: datasetId,
              annotation_set_id: annotationSetId,
              annotation_id: annotationId,
            },
            body: data,
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to update annotation'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.list(datasetId, annotationSetId),
      });
    },
  });
}

/**
 * Delete an annotation.
 */
export function useDeleteAnnotation(
  datasetId: string,
  annotationSetId: string
) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (annotationId: string) => {
      const response =
        await deleteAnnotationApiV1DatasetsDatasetIdAnnotationSetsAnnotationSetIdAnnotationsAnnotationIdDelete(
          {
            path: {
              dataset_id: datasetId,
              annotation_set_id: annotationSetId,
              annotation_id: annotationId,
            },
          }
        );
      if (response.error) {
        throw new Error(
          (response.error as { detail?: string }).detail ||
            'Failed to delete annotation'
        );
      }
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: annotationKeys.list(datasetId, annotationSetId),
      });
      queryClient.invalidateQueries({
        queryKey: annotationSetKeys.detail(datasetId, annotationSetId),
      });
    },
  });
}
